import React, { useState, useEffect } from 'react';
import './Portfolio.css';
import portfolioData from './portfolio-data.json';

// Utility function to get text in current language
const getText = (data, lang) => {
  if (typeof data === 'string') return data;
  if (typeof data === 'object' && data !== null) {
    return data[lang] || data.en || data;
  }
  return data;
};

// Header Component
const Header = ({ data, lang, buttons }) => {
  return (
    <header className="hero">
      <div className="hero-content">
        <div className="hero-badge">{getText(data.badge, lang)}</div>
        <h1>{data.name}</h1>
        <p className="subtitle">{getText(data.subtitle, lang)}</p>
        <div className="cta-buttons">
          <a href="#projects" className="btn btn-primary">{getText(buttons.viewWork, lang)}</a>
          <a href={`mailto:${data.email}`} className="btn btn-secondary">{getText(buttons.getInTouch, lang)}</a>
        </div>
      </div>
    </header>
  );
};

// Navigation Component
const Navigation = ({ lang, setLang, navText }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={scrolled ? 'scrolled' : ''}>
      <div className="container">
        <div className="nav-logo">MG</div>
        <ul>
          <li><a href="#about">{getText(navText.about, lang)}</a></li>
          <li><a href="#skills">{getText(navText.skills, lang)}</a></li>
          <li><a href="#experience">{getText(navText.experience, lang)}</a></li>
          <li><a href="#projects">{getText(navText.projects, lang)}</a></li>
        </ul>
        <div className="language-switcher">
          <button 
            className={`lang-btn ${lang === 'en' ? 'active' : ''}`} 
            onClick={() => setLang('en')}
          >
            EN
          </button>
          <button 
            className={`lang-btn ${lang === 'fr' ? 'active' : ''}`} 
            onClick={() => setLang('fr')}
          >
            FR
          </button>
        </div>
      </div>
    </nav>
  );
};

// About Section
const About = ({ data, lang, section }) => {
  const bioArray = getText(data.bio, lang);
  
  return (
    <section id="about">
      <div className="container">
        <div className="section-header">
          <span className="section-badge">{getText(section.badge, lang)}</span>
          <h2>{getText(section.title, lang)}</h2>
          <p className="section-description">{getText(section.description, lang)}</p>
        </div>
        <div className="about-grid">
          <div className="about-content">
            {Array.isArray(bioArray) && bioArray.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
          <div className="stats">
            {data.stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <h3>{stat.value}</h3>
                <p>{getText(stat.label, lang)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Skills Section
const Skills = ({ skills, lang, section }) => {
  return (
    <section id="skills">
      <div className="container">
        <div className="section-header">
          <span className="section-badge">{getText(section.badge, lang)}</span>
          <h2>{getText(section.title, lang)}</h2>
          <p className="section-description">{getText(section.description, lang)}</p>
        </div>
        <div className="skills-grid">
          {skills.map((skillCategory, index) => (
            <div key={index} className="skill-category">
              <h3>
                <span className="skill-icon">{skillCategory.icon}</span>
                {getText(skillCategory.category, lang)}
              </h3>
              <ul>
                {skillCategory.items.map((skill, skillIndex) => (
                  <li key={skillIndex}>{getText(skill, lang)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Experience Section
const Experience = ({ experiences, lang, section }) => {
  return (
    <section id="experience">
      <div className="container">
        <div className="section-header">
          <span className="section-badge">{getText(section.badge, lang)}</span>
          <h2>{getText(section.title, lang)}</h2>
          <p className="section-description">{getText(section.description, lang)}</p>
        </div>
        <div className="experience-timeline">
          {experiences.map((exp, index) => {
            const description = getText(exp.description, lang);
            return (
              <div key={index} className="experience-item">
                <h3>{getText(exp.title, lang)}</h3>
                <div className="company">{getText(exp.company, lang)}</div>
                <div className="date">{getText(exp.period, lang)}</div>
                <ul>
                  {Array.isArray(description) && description.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Projects Section
const Projects = ({ projects, lang, section, buttons }) => {
  return (
    <section id="projects">
      <div className="container">
        <div className="section-header">
          <span className="section-badge">{getText(section.badge, lang)}</span>
          <h2>{getText(section.title, lang)}</h2>
          <p className="section-description">{getText(section.description, lang)}</p>
        </div>
        <div className="projects-grid">
          {projects.map((project, index) => (
            <div key={index} className="project-card">
              <div className="project-content">
                <h3>{getText(project.title, lang)}</h3>
                <p>{getText(project.description, lang)}</p>
                <div className="project-tags">
                  {project.technologies.map((tech, techIndex) => (
                    <span key={techIndex} className="tag">{getText(tech, lang)}</span>
                  ))}
                </div>
                
                {/* Support for multiple links */}
                <div className="project-links">
                  {project.links ? (
                    // Multiple links
                    project.links.map((linkObj, linkIndex) => (
                      <a 
                        key={linkIndex}
                        href={linkObj.url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="project-link"
                      >
                        {getText(linkObj.text, lang)} →
                      </a>
                    ))
                  ) : (
                    // Single link (backward compatibility)
                    <a href={project.link} target="_blank" rel="noopener noreferrer" className="project-link">
                      {getText(buttons[project.linkText], lang)} →
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = ({ data, lang, footer, navText }) => {
  return (
    <footer>
      <div className="container">
        <div className="footer-content">
          <div className="footer-about">
            <h3>{data.name}</h3>
            <p>{getText(footer.description, lang)}</p>
            <div className="social-links">
              <a href={data.social.github} target="_blank" rel="noopener noreferrer" className="social-link" title="GitHub">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </a>
              <a href={data.social.linkedin} target="_blank" rel="noopener noreferrer" className="social-link" title="LinkedIn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
              <a href={`mailto:${data.email}`} className="social-link" title="Email">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M0 3v18h24v-18h-24zm6.623 7.929l-4.623 5.712v-9.458l4.623 3.746zm-4.141-5.929h19.035l-9.517 7.713-9.518-7.713zm5.694 7.188l3.824 3.099 3.83-3.104 5.612 6.817h-18.779l5.513-6.812zm9.208-1.264l4.616-3.741v9.348l-4.616-5.607z"/>
                </svg>
              </a>
              <a href={`tel:${data.phone}`} className="social-link" title="Phone">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 22.621l-3.521-6.795c-.008.004-1.974.97-2.064 1.011-2.24 1.086-6.799-7.82-4.609-8.994l2.083-1.026-3.493-6.817-2.106 1.039c-7.202 3.755 4.233 25.982 11.6 22.615.121-.055 2.102-1.029 2.11-1.033z"/>
                </svg>
              </a>
            </div>
          </div>
          <div className="footer-links">
            <h4>{getText(footer.quickLinks, lang)}</h4>
            <ul>
              <li><a href="#about">{getText(navText.about, lang)}</a></li>
              <li><a href="#skills">{getText(navText.skills, lang)}</a></li>
              <li><a href="#experience">{getText(navText.experience, lang)}</a></li>
              <li><a href="#projects">{getText(navText.projects, lang)}</a></li>
            </ul>
          </div>
          <div className="footer-links">
            <h4>{getText(footer.contact, lang)}</h4>
            <ul>
              <li><a href={`mailto:${data.email}`}>{data.email}</a></li>
              <li><a href={`tel:${data.phone}`}>{data.phone}</a></li>
              <li>{data.location}</li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 {data.name}. {getText(footer.rights, lang)}</p>
        </div>
      </div>
    </footer>
  );
};

// Scroll to Top Button
const ScrollToTop = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className={`scroll-top ${visible ? 'visible' : ''}`} onClick={scrollToTop}>
      ↑
    </div>
  );
};

// Main App Component
function Portfolio() {
  const [lang, setLang] = useState('en');
  const { personalInfo, skills, experiences, projects, sections, navigation, buttons, footer } = portfolioData;

  // Load saved language preference
  useEffect(() => {
    const savedLang = localStorage.getItem('preferredLanguage') || 'en';
    setLang(savedLang);
  }, []);

  // Save language preference when it changes
  useEffect(() => {
    localStorage.setItem('preferredLanguage', lang);
  }, [lang]);

  useEffect(() => {
    // Smooth scroll for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const targetId = this.getAttribute('href');
        if (targetId === '#') return;
        const targetSection = document.querySelector(targetId);
        if (targetSection) {
          targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }, []);

  return (
    <div className="App">
      <div className="bg-animation"></div>
      <Header data={personalInfo} lang={lang} buttons={buttons} />
      <Navigation lang={lang} setLang={setLang} navText={navigation} />
      <About data={personalInfo} lang={lang} section={sections.about} />
      <Skills skills={skills} lang={lang} section={sections.skills} />
      <Experience experiences={experiences} lang={lang} section={sections.experience} />
      <Projects projects={projects} lang={lang} section={sections.projects} buttons={buttons} />
      <Footer data={personalInfo} lang={lang} footer={footer} navText={navigation} />
      <ScrollToTop />
    </div>
  );
}

export default Portfolio;
